import "yet-another-react-lightbox/styles.css";

import {
  ArrowRightIcon,
  BookOpenIcon,
  CheckBadgeIcon,
  GlobeAmericasIcon,
  HandRaisedIcon,
  HeartIcon,
  SparklesIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { GalleryImage, LightboxState } from '../types';
import React, { useState } from 'react';

import Lightbox from 'yet-another-react-lightbox';
import { Link } from 'react-router-dom';

const Home: React.FC = () => {
  // Lightbox state
  const [lightbox, setLightbox] = useState<LightboxState>({ open: false, index: 0 });
  
  // Gallery images data
  const galleryImages: GalleryImage[] = Array.from({ length: 23 }, (_, i) => ({
    src: `/assets/blassedcharity-${i + 1}.jpg`,
    alt: `Blessed Gideon Charity - Impact Image ${i + 1}`,
    width: 1200,
    height: 800
  }));
  const features = [
    {
      name: 'Community Support',
      description: 'Delivering food, shelter, and essential resources to families and individuals in underserved communities.',
      icon: UsersIcon,
      color: 'ocean'
    },
    {
      name: 'Child Welfare',
      description: 'Providing comprehensive care for vulnerable children including safe housing, education, and healthcare.',
      icon: HeartIcon,
      color: 'coral'
    },
    {
      name: 'Educational Programs',
      description: 'Offering scholarships, literacy initiatives, and vocational training to empower future generations.',
      icon: BookOpenIcon,
      color: 'gold'
    },
    {
      name: 'Global Outreach',
      description: 'Extending our mission worldwide to create sustainable impact in communities that need it most.',
      icon: GlobeAmericasIcon,
      color: 'navy'
    },
  ];

  const achievements = [
    { icon: CheckBadgeIcon, title: 'Families Supported', value: '315+', description: 'Direct beneficiaries' },
    { icon: HandRaisedIcon, title: 'Active Volunteers', value: '100+', description: 'Community helpers' },
    { icon: SparklesIcon, title: 'Programs Running', value: '5', description: 'Active initiatives' },
    { icon: HeartIcon, title: 'Years of Impact', value: '9', description: 'Serving communities' },
  ];

  return (
    <React.Fragment>
      <div className="min-h-screen pt-20">
      {/* Hero Section with Floating Elements */}
      <section className="relative flex items-center min-h-screen overflow-hidden">
        {/* Hero background image */}
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="/assets/blassedcharity-2.jpg" 
            alt="Hero Banner" 
            className="object-cover w-full h-full" 
            style={{filter: 'brightness(0.65)'}} 
          />
        </div>
        {/* Overlay for gradient and floating shapes */}
        <div className="absolute inset-0 gradient-mesh opacity-80"></div>
        {/* Floating geometric shapes */}
        <div className="absolute w-20 h-20 rounded-full top-20 left-10 bg-coral-400 opacity-20 floating-card"></div>
        <div className="absolute top-40 right-20 w-32 h-32 bg-gold-400 rounded-lg opacity-15 floating-card delay-[2s]"></div>
        <div className="absolute bottom-40 left-20 w-16 h-16 bg-ocean-400 rounded-full opacity-25 floating-card delay-[4s]"></div>
        {/* Hero content */}
        <div className="relative z-10 text-center text-white container-custom">
          <div className="max-w-5xl mx-auto">
            <h1 className="mb-8 text-4xl font-black leading-tight md:text-7xl lg:text-8xl font-heading drop-shadow-lg">
              <span className="block">Transforming</span>
              <span className="font-accent text-gold-400">Lives</span>
              <span className="block">Together</span>
            </h1>
            <p className="max-w-4xl mx-auto mb-12 text-xl leading-relaxed md:text-2xl lg:text-3xl font-body text-navy-100 drop-shadow">
              Join us in fighting poverty, caring for the less priviledged, and empowering communities through compassion and sustainable solutions.
            </p>
            <div className="flex flex-col items-center justify-center gap-6 sm:flex-row">
              <Link to="/contact" className="px-12 py-6 text-xl btn-coral">
                Start Your Journey
                <ArrowRightIcon className="w-6 h-6 ml-3" />
              </Link>
              <Link to="/about" className="flex items-center justify-center px-12 py-6 text-xl text-white border-white btn-outline-navy hover:bg-white hover:text-navy-800">
                Watch Our Story
              </Link>
            </div>
          </div>
        </div>
        {/* Scroll indicator */}
        <div className="absolute transform -translate-x-1/2 bottom-8 left-1/2 animate-bounce">
          <div className="flex justify-center w-6 h-10 border-2 border-white rounded-full">
            <div className="w-1 h-3 mt-2 bg-white rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Stats Section with Cards */}
      <section className="section-padding bg-navy-50">
        <div className="container-custom">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl font-bold md:text-5xl font-heading text-gradient">
              Our Impact in Numbers
            </h2>
            <p className="max-w-3xl mx-auto text-xl text-navy-600">
              Every number represents a life touched, a community strengthened, and hope restored.
            </p>
          </div>
          
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {achievements.map((achievement, index) => (
              <div 
                key={achievement.title} 
                className={`card p-8 text-center group hover:bg-gradient-to-br hover:from-white hover:to-navy-50 animate-fade-in-scale delay-${index * 200}`}
              >
                <div className="mb-6">
                  <div className="flex items-center justify-center w-16 h-16 mx-auto transition-transform duration-300 transform bg-gradient-to-br from-ocean-400 to-ocean-600 rounded-2xl group-hover:scale-110">
                    <achievement.icon className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="mb-2 text-4xl font-black font-heading text-navy-800">
                  {achievement.value}
                </div>
                <h3 className="mb-2 text-lg font-semibold font-heading text-navy-700">
                  {achievement.title}
                </h3>
                <p className="text-navy-500 font-body">
                  {achievement.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section with Split Layout */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <div className="grid items-center grid-cols-1 gap-16 lg:grid-cols-2">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-coral-400 to-ocean-400 rounded-3xl opacity-20 blur-xl"></div>
                <div className="relative p-8 bg-white border shadow-2xl rounded-3xl border-navy-100">
                  <blockquote className="text-2xl italic leading-relaxed md:text-3xl font-accent text-navy-800">
                    "Every act of compassion creates ripples of change that extend far beyond what we can see."
                  </blockquote>
                  <div className="flex items-center mt-6">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-coral-400 to-coral-600">
                      <HeartIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <div className="font-semibold font-heading text-navy-800">Our Foundation</div>
                      <div className="text-navy-600 font-body">Built on love and service</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <h2 className="mb-8 text-4xl font-bold md:text-5xl font-heading text-navy-800">
                Our <span className="text-gradient">Mission</span>
              </h2>
              <div className="space-y-6 text-lg leading-relaxed text-navy-600 font-body">
                <p>
                  Blessed Gideon Charity is dedicated to alleviating poverty and supporting families in underserved communities worldwide. We believe everyone deserves a safe home, education, and hope for the future.
                </p>
                <p>
                  Our mission is to provide essential resources, loving care, and opportunities for growth to the most vulnerable, empowering them to thrive and build resilient communities.
                </p>
                <p>
                  By focusing our efforts globally, we aim to create sustainable change and a brighter tomorrow for generations to come.
                </p>
              </div>
              <div className="mt-8">
                <Link to="/about" className="inline-flex items-center btn-navy">
                  Learn More About Us
                  <ArrowRightIcon className="w-5 h-5 ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section with Card Grid and Mobile-First Image Gallery */}
      <section className="relative overflow-hidden section-padding bg-gradient-to-br from-navy-900 via-navy-800 to-ocean-900">
        {/* Featured background image for programs section */}
        <div className="absolute inset-0 opacity-20">
          <img src="/assets/blassedcharity-1.jpg" alt="Blessed Charity Featured" className="object-cover w-full h-full" />
        </div>
        <div className="relative z-10 container-custom">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-white sm:text-4xl md:text-5xl font-heading">
              How We <span className="text-gold-400">Serve</span>
            </h2>
            <p className="max-w-2xl mx-auto text-base sm:text-lg md:text-xl text-navy-100">
              Through comprehensive programs designed to address root causes and create lasting impact.
            </p>
          </div>
          {/* Mobile-first image gallery with lightbox */}
          <div className="grid grid-cols-2 gap-3 mb-12 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {galleryImages.map((image, index) => (
              <div 
                key={index} 
                className="relative overflow-hidden shadow-lg cursor-pointer rounded-xl group"
                onClick={() => setLightbox({ open: true, index })}
              >
                <img 
                  src={image.src} 
                  alt={image.alt} 
                  className="object-cover w-full h-24 transition-all duration-300 sm:h-32 md:h-40 lg:h-44 hover:scale-105 group-hover:brightness-110" 
                  loading="lazy"
                />
                {/* Hover overlay with click indicator */}
                <div className="absolute inset-0 flex items-center justify-center transition-opacity duration-300 bg-black bg-opacity-0 opacity-0 group-hover:opacity-100 group-hover:bg-opacity-40">
                  <div className="p-2 bg-white rounded-full">
                    <svg className="w-6 h-6 text-navy-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Programs grid */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {features.map((feature, index) => {
              const colorClasses = {
                ocean: 'from-ocean-500 to-ocean-700',
                coral: 'from-coral-500 to-coral-700',
                gold: 'from-gold-500 to-gold-700',
                navy: 'from-navy-600 to-navy-800'
              };
              return (
                <div 
                  key={feature.name} 
                  className={`group p-5 sm:p-8 rounded-3xl bg-white/10 backdrop-blur-lg border border-white/20 hover:bg-white/20 transition-all duration-500 transform hover:-translate-y-2 animate-fade-in-scale`}
                  style={{animationDelay: `${index * 150}ms`}}
                >
                  <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start sm:gap-6">
                    <div className={`flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br ${colorClasses[feature.color as keyof typeof colorClasses]} rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300`}>
                      <feature.icon className="text-white w-7 h-7 sm:w-8 sm:h-8" />
                    </div>
                    <div className="flex-1 text-center sm:text-left">
                      <h3 className="mb-2 text-lg font-bold text-white transition-colors duration-300 sm:text-2xl font-heading group-hover:text-gold-300">
                        {feature.name}
                      </h3>
                      <p className="text-xs leading-relaxed sm:text-base text-navy-200 font-body">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Call to Action with Unique Design */}
      <section className="relative overflow-hidden text-white section-padding bg-gradient-to-r from-coral-500 via-coral-600 to-ocean-600">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute w-32 h-32 rounded-full top-10 left-10 bg-white/10 blur-xl"></div>
          <div className="absolute w-40 h-40 rounded-full bottom-10 right-10 bg-white/10 blur-xl"></div>
          <div className="absolute transform -translate-x-1/2 -translate-y-1/2 rounded-full top-1/2 left-1/2 w-60 h-60 bg-white/5 blur-2xl"></div>
        </div>
        
        <div className="relative text-center container-custom">
          <h2 className="mb-8 text-4xl font-black md:text-6xl font-heading">
            Ready to Make a <span className="font-accent">Difference?</span>
          </h2>
          <p className="max-w-4xl mx-auto mb-12 text-xl leading-relaxed md:text-2xl text-coral-100">
            Whether you're looking to volunteer your time, make a donation, or partner with us, 
            every contribution helps us create a more compassionate world.
          </p>
          <div className="flex flex-col items-center justify-center gap-6 sm:flex-row">
            <Link 
              to="/contact" 
              className="px-12 py-6 text-xl transition-all duration-300 transform btn-gold hover:scale-105"
            >
              Get Involved Today
              <ArrowRightIcon className="w-6 h-6 ml-3" />
            </Link>
            <button className="px-12 py-6 text-xl text-white border-white btn-outline-navy hover:bg-white hover:text-navy-800">
              Support Our Mission
            </button>
          </div>
        </div>
      </section>
    </div>

    {/* Lightbox for gallery images */}
    <Lightbox
      open={lightbox.open}
      close={() => setLightbox({ ...lightbox, open: false })}
      index={lightbox.index}
      slides={galleryImages}
      animation={{ fade: 300 }}
      carousel={{
        finite: true,
        preload: 2,
      }}
      render={{
        buttonPrev: galleryImages.length <= 1 ? () => null : undefined,
        buttonNext: galleryImages.length <= 1 ? () => null : undefined,
      }}
      controller={{
        closeOnBackdropClick: true,
        closeOnPullUp: true,
        closeOnPullDown: true,
      }}
      styles={{
        container: { backgroundColor: "rgba(0, 0, 0, 0.9)" },
      }}
      on={{
        view: ({ index }) => setLightbox(prev => ({ ...prev, index })),
      }}
    />
    </React.Fragment>
    );
  };


export default Home;

